import styled from 'styled-components';
import { Placeholder, Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { lastminuteDealsGradientOnText } from '@/app/common/components/Promotions/styled';
import { getTotalPerNightsLabel } from '@/app/components/search/components/Card/utils/getPriceDetails';
import { StrikeThroughPrice } from '@/app/components/hotel-detail/common/StrikeThroughPrice';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';
import TranslateSafeText from '@/app/common/components/TranslateSafeText';
import { LocalTaxes } from './rooms/LocalTaxes';
import { useInitialParams } from './InitialParamsProvider';
import { useTranslation } from '../translations/Translation';
import { useOffersContext } from './OffersProvider';
import { useUrlSearchParams } from './UrlSearchParamsProvider';

const TotalPricePerNight = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 4px;
`;

export function PriceSummary(
  props: { mealPlan: MealPlanType } & {
    showFromLabel?: boolean;
    showLmnDeal?: boolean;
    showStrikeThroughPrice?: boolean;
  } & React.HTMLAttributes<HTMLElement>
) {
  const { mealPlan, showFromLabel = false, showLmnDeal = false, showStrikeThroughPrice = false, ...rest } = props;
  const { offersData } = useOffersContext();
  const { priceType } = useInitialParams();
  const { localization } = usePageWrapperData();
  // In the EVO variation the price-type label ("pp") is BodySmall bold everywhere; the legacy flow keeps Caption.
  const evolutionEnabled = localization?.configValuesApp?.evolutionEnabled || false;
  const { totalGuestsWithoutInfants, accommodationOnly } = useUrlSearchParams();
  const { t } = useTranslation();
  const { showTotalPrice: showTotal, toDisplayPrice } = usePriceDisplay();
  const nights = offersData?.nights;

  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
      `}
      {...rest}
    >
      <div
        css={`
          display: flex;
          gap: 2px;
          align-items: baseline;
          flex-wrap: wrap;
        `}
      >
        {showStrikeThroughPrice && mealPlan.strikeThroughPrice ? (
          <StrikeThroughPrice
            css={`
              margin-right: 6px;
            `}
          >
            {formatPrice({ price: toDisplayPrice(mealPlan.strikeThroughPrice), localization })}
          </StrikeThroughPrice>
        ) : undefined}
        {showFromLabel && (
          <Text
            variant="BodySmall"
            css={`
              padding-right: 4px;
            `}
          >
            {t('vacation.common.price.from')}
          </Text>
        )}
        <Price
          data-testid="price-info"
          data-price="regular"
          css={showLmnDeal ? lastminuteDealsGradientOnText : undefined}
        >
          {/* Coming back from change flight page we want to show the loading status while getting new prices */}
          {formatPrice({ price: showTotal ? mealPlan.finalTotalPrice : mealPlan.finalPrice, localization })}
        </Price>
        <PriceTypeLabelContainer data-testid="price-type" $emphasized={evolutionEnabled}>
          {showTotal ? (
            t('vacation.oss.cards.total')
          ) : (
            <PriceTypeLabel
              priceType={priceType}
              roomsIncludedInPrice={offersData?.roomsIncludedInPriceForCheapestRoom}
            />
          )}
        </PriceTypeLabelContainer>
      </div>
      {accommodationOnly && (
        <TotalPricePerNight>
          <Text variant="Caption" weight="bold">
            {getTotalPerNightsLabel({ nights, t })}
          </Text>
        </TotalPricePerNight>
      )}
      <LocalTaxes
        nights={offersData?.nights}
        passengers={totalGuestsWithoutInfants}
        roomsIncludedInPrice={offersData?.roomsIncludedInPriceForCheapestRoom || 1}
        excludedCharges={mealPlan.excludedCharges}
        excludedChargesIncluded={mealPlan.excludedChargesIncluded}
        price={mealPlan.finalTotalPrice}
        priceWithoutTaxes={mealPlan.priceWithoutTaxes}
        showTotalPriceIncludedTaxes={true}
      />
    </div>
  );
}

const Price = styled(Text).attrs({ variant: 'Title3' })``;

const PriceTypeLabelContainer = styled(Text).attrs<{ $emphasized?: boolean }>((p) => ({
  variant: p.$emphasized ? 'BodySmall' : 'Caption',
  weight: p.$emphasized ? 'bold' : undefined,
}))`
  white-space: nowrap;
`;

export function PriceTypeLabel(props: { priceType: PriceType; roomsIncludedInPrice?: number }) {
  const { priceType, roomsIncludedInPrice } = props;
  const { t } = useTranslation();

  const label =
    priceType === 'PER_PERSON'
      ? t('vacation.select.room.price.dp.perperson.short')
      : priceType === 'TOTAL'
        ? ''
        : roomsIncludedInPrice === 1
          ? t('vacation.select.room.price.includes.pernight.short')
          : t('vacation.select.room.price.includes.perroom.and.night.short');

  return label ? <TranslateSafeText>{label}</TranslateSafeText> : null;
}

export function PriceSkeletons() {
  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 4px 0;
      `}
    >
      <Placeholder height="18px" width="100px" />
      <Placeholder height="12px" width="80px" />
    </div>
  );
}
